import React, { ReactElement, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Skeleton,
  Stack,
  CardHeader,
  IconButton,
} from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { RiskOverviewService } from 'services/RiskOverviewService';
import { Utils } from 'shared/helpers';
import { useNavigate } from 'react-router-dom';

interface RiskTierData {
  riskTier: string;
  count: number;
}

interface OpenRisksData {
  openRisks: number;
  risksRequiringAttention: number;
}

interface AIRiskManagementOverviewProps {
  aiFilter?: string;
  dateRange?: {
    from: string;
    to: string;
  };
}

const riskTierOrder = ['Low', 'Moderate', 'High', 'Critical'];
const riskTierColor: Record<
  string,
  { bg: string; color: string; border: string }
> = {
  Low: { bg: '#dcf5e7', color: '#1a5c35', border: '#b8e0cc' },
  Moderate: { bg: '#fdf6cc', color: '#7a5e0a', border: '#f5e8a3' },
  High: { bg: '#fde8cc', color: '#8b3a0f', border: '#f5d4a3' },
  Critical: { bg: '#fde0e0', color: '#8b1515', border: '#f5c4c4' },
};

const RiskTierCard = ({
  tier,
  count,
}: {
  tier: string;
  count: number;
}): ReactElement => {
  const colors = riskTierColor[tier] || riskTierColor.Low;

  return (
    <Box
      sx={{
        background: colors.bg,
        borderRadius: 2,
        padding: '16px 20px',
        textAlign: 'center',
        minWidth: 100,
        border: `1px solid ${colors.border}`,
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontSize: 28,
          fontWeight: 700,
          color: colors.color,
          lineHeight: 1.2,
        }}
      >
        {count}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: colors.color,
          marginTop: 1,
          fontSize: 14,
        }}
      >
        {tier}
      </Typography>
    </Box>
  );
};

const RiskOverview = ({
  aiFilter = 'All',
  dateRange,
}: AIRiskManagementOverviewProps): ReactElement => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [openRisksData, setOpenRisksData] = useState<OpenRisksData>({
    openRisks: 0,
    risksRequiringAttention: 0,
  });
  const [riskTiers, setRiskTiers] = useState<RiskTierData[]>([]);

  const fetchRiskOverview = async (): Promise<void> => {
    setLoading(true);

    try {
      const params = new URLSearchParams();
      if (aiFilter && aiFilter !== 'All') {
        params.append('aiType', aiFilter);
      }
      if (dateRange?.from) {
        params.append('fromDate', dateRange.from);
      }
      if (dateRange?.to) {
        params.append('toDate', dateRange.to);
      }

      const queryString = params.toString() ? `?${params.toString()}` : '';

      const response = await RiskOverviewService.getAll(
        queryString,
        'LAST_12_MONTHS'
      );
      const data = response?.data?.data || response?.data;
      setOpenRisksData({
        openRisks: data?.riskOverview?.openRisks || 0,
        risksRequiringAttention:
          data?.riskOverview?.risksRequiringAttention || 0,
      });

      const tiers = data?.aiSystemByRiskTier || [];
      const sortedTiers = [...tiers].sort(
        (a: RiskTierData, b: RiskTierData) =>
          riskTierOrder.indexOf(a.riskTier) - riskTierOrder.indexOf(b.riskTier)
      );
      setRiskTiers(sortedTiers);
    } catch (err) {
      console.error('Error fetching risk overview:', err);
      Utils.handleErrorResponse(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRiskOverview();
  }, [aiFilter, dateRange?.from, dateRange?.to]);
  return (
    <Box>
      <Card variant="outlined">
        <CardHeader
          title="Risk Overview"
          subheader="Monitor and analyze your organisation's risk landscape"
          action={
            <IconButton
              onClick={() => {
                navigate(`/risk-overview`);
              }}
            >
              <RemoveRedEyeIcon />
            </IconButton>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={3} lg={2}>
              <Box
                sx={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  p: 2.5,
                  height: '100%',
                  backgroundColor: '#ffffff',
                  transition: 'box-shadow 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  },
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#374151',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Open Risks
                  </Typography>
                </Stack>

                {loading ? (
                  <Skeleton variant="text" width={80} height={50} />
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontSize: 42,
                        fontWeight: 700,
                        color: '#052921',
                        lineHeight: 1.1,
                        mb: 1,
                      }}
                    >
                      {openRisksData.openRisks}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: '#6b7280',
                        mt: 2,
                      }}
                    >
                      Risks requiring attention
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} sm={8} md={9} lg={10}>
              <Box
                sx={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  p: 2.5,
                  height: '100%',
                  backgroundColor: '#ffffff',
                  transition: 'box-shadow 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  },
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6">AI Systems by Risk Tier</Typography>
                </Stack>

                {loading ? (
                  <Grid container spacing={2}>
                    {[1, 2, 3, 4].map((i) => (
                      <Grid key={i} item xs={6} lg={3}>
                        <Skeleton
                          variant="rectangular"
                          height={90}
                          sx={{ borderRadius: 2 }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <>
                    <Grid
                      container
                      spacing={2}
                      sx={{ justifyContent: 'space-around' }}
                    >
                      {riskTiers.map(
                        (tier) =>
                          tier.riskTier !== 'Medium' && (
                            <Grid key={tier.riskTier} item xs={6} lg={3}>
                              <RiskTierCard
                                tier={tier.riskTier}
                                count={tier.count}
                              />
                            </Grid>
                          )
                      )}
                    </Grid>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: '#6b7280',
                        mt: 2,
                        textAlign: 'center',
                      }}
                    >
                      Active AI systems categorized by assigned risk level
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RiskOverview;
